import { formatISO, startOfDay, addDays, getDay, startOfMonth, lastDayOfMonth } from 'date-fns';

const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

export function format(date) {
  return formatISO(date, { representation: 'date' });
}

function getFormattedDate(date) {
  return format(startOfDay(date));
}

function getPreviousDate(days, date = new Date()) {
  const yesterday = new Date(date);
  yesterday.setDate(yesterday.getDate() - days);
  return yesterday;
}

function getNextDate(days, date = new Date()) {
  return addDays(date, days);
}

function getWeek(date) {
  const today = getDay(date);
  const from = getFormattedDate(getPreviousDate(today, date));
  const to = getFormattedDate(getNextDate(6 - today, date));
  return { from, to };
}

function getMonth(date) {
  const from = format(startOfMonth(date));
  const to = format(lastDayOfMonth(date));
  return { from, to };
}

export function getToday() {
  const today = new Date();
  return getFormattedDate(today);
}

export function getYesterday() {
  const yesterday = getPreviousDate(1);
  return getFormattedDate(yesterday);
}

export function getTomorrow() {
  const tomorrow = getNextDate(1);
  return getFormattedDate(tomorrow);
}

export function getCurrentWeek() {
  return getWeek(new Date());
}

export function getLastWeek() {
  return getWeek(getPreviousDate(7));
}

export function getCurrentMonth() {
  return getMonth(new Date());
}

export function getLastMonth() {
  const date = new Date();
  const lastMonthDate = new Date(date.getFullYear(), date.getMonth(), 0);
  return getMonth(lastMonthDate);
}

/**
 * Serializes a datetime value to ISO 8601 UTC string (YYYY-MM-DDTHH:mm:ss.sssZ).
 * Used for DATE_TIME form fields to match backend format. When `timeZone` is provided, `value` is treated as
 * wall-clock time in that timezone (e.g. picked in a timezone-aware date-time picker) and converted to the
 * correct UTC instant; otherwise behaves exactly as before (plain `new Date(value)`).
 * @param {string|number|Date} value - Value to serialize
 * @param {string} [timeZone] - IANA timezone name (e.g. 'America/New_York')
 * @returns {string|*} ISO string or original value if invalid/empty
 */
export function serializeDateTimeToISO(value, timeZone) {
  if (!value) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
    return value;
  }
  const date = timeZone ? zonedTimeToUtc(value, timeZone) : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
}

/**
 * Returns a Date whose local getters (getFullYear/getHours/etc.) reflect the wall-clock time in `timeZone`
 * for the given UTC-based value. Used to *display* a stored UTC value as the target timezone's local time
 * (e.g. in a date-time picker that only understands the browser's own local time).
 * Native Intl-based - no extra dependency, matches the `timeZone` option already used by CommonUtils.timeSince.
 * @param {string|number|Date} value
 * @param {string} [timeZone] - IANA timezone name (e.g. 'America/New_York')
 * @returns {Date}
 */
export function utcToZonedTime(value, timeZone) {
  const date = new Date(value);
  if (!timeZone || Number.isNaN(date.getTime())) return date;
  return new Date(date.toLocaleString('en-US', { timeZone }));
}

/**
 * Returns how far ahead of UTC `timeZone`'s wall-clock is, in milliseconds, at the given instant (e.g. -300*60000
 * for EST/UTC-5). Uses Intl.DateTimeFormat.formatToParts to read `timeZone`'s wall-clock components directly,
 * rather than round-tripping through a locale string re-parsed as the *runtime's own* local time (a common
 * pitfall - re-parsing that way silently produces the wrong offset whenever the runtime's own timezone isn't UTC).
 * @param {Date} date
 * @param {string} timeZone - IANA timezone name
 * @returns {number}
 */
function getTimeZoneOffsetMs(date, timeZone) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return asUTC - date.getTime();
}

/**
 * Inverse of utcToZonedTime: given a value whose local getters represent a wall-clock time intended to be in
 * `timeZone`, returns the equivalent UTC instant.
 * @param {string|number|Date} value
 * @param {string} [timeZone] - IANA timezone name (e.g. 'America/New_York')
 * @returns {Date}
 */
export function zonedTimeToUtc(value, timeZone) {
  const date = new Date(value);
  if (!timeZone || Number.isNaN(date.getTime())) return date;
  // Treat the input's local getters as if they were already UTC, as an anchor instant close enough to the
  // real one to compute timeZone's offset at that moment (accurate except right at a DST transition edge).
  const asUTC = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  const offsetMs = getTimeZoneOffsetMs(asUTC, timeZone);
  return new Date(asUTC.getTime() - offsetMs);
}

const MOMENT_TO_DATE_FNS_TOKENS = [
  ['YYYY', 'yyyy'],
  ['YY', 'yy'],
  ['DD', 'dd'],
  ['D', 'd'],
  ['A', 'a'],
];

/**
 * Translates a moment.js-style format token string (e.g. 'MM/DD/YYYY') into the equivalent date-fns v2 token
 * string, since crayons' fw-datepicker displayFormat/timeFormat props expect date-fns tokens. MM/M/HH/H/hh/h/
 * mm/m/ss/s are identical between the two conventions, so only the differing tokens are substituted, longest
 * first so YYYY isn't partially clobbered by a YY replacement.
 * @param {string} momentFormat
 * @returns {string}
 */
export function convertMomentFormatToDateFns(momentFormat) {
  if (!momentFormat) return momentFormat;
  return MOMENT_TO_DATE_FNS_TOKENS.reduce((result, [momentToken, dateFnsToken]) => result.split(momentToken).join(dateFnsToken), momentFormat);
}

/**
 * Splits a combined moment.js-style dateTimeFormat (e.g. 'MM/DD/YYYY hh:mm A', as sent by the host in
 * data.user.locale.dateTimeFormat) into separate date-fns format strings for a date-time picker's
 * displayFormat/timeFormat props. Assumes the date and time portions are separated by whitespace.
 * @param {string} dateTimeFormat
 * @returns {{datePart: string|null, timePart: string|null}}
 */
export function splitDateTimeFormat(dateTimeFormat) {
  if (!dateTimeFormat) return { datePart: null, timePart: null };
  const dateSegments = [];
  const timeSegments = [];
  dateTimeFormat
    .trim()
    .split(/\s+/)
    .forEach(segment => {
      const isTimeSegment = /[Hhms]/.test(segment) || segment === 'A' || segment === 'a';
      (isTimeSegment ? timeSegments : dateSegments).push(segment);
    });
  return {
    datePart: dateSegments.length ? convertMomentFormatToDateFns(dateSegments.join(' ')) : null,
    timePart: timeSegments.length ? convertMomentFormatToDateFns(timeSegments.join(' ')) : null,
  };
}
